/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import getInvoicePdf from "@eisinc/App/Handlers/GetInvoicePdf";
import { actionOverflowMenu } from "@eisinc/App/SharedStyles/GeneralStyles";
import { iconsSharedStyles } from "@eisinc/App/SharedStyles/IconsStyles";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Modal from "@eisinc/MobiusOverrides/Modal";
import Invoices2 from "@eisinc/XDL/Icons/Invoices2";
import Print from "@eisinc/XDL/Icons/Print";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
});

const mapDispatchToProps = {
    getInvoicePdf,
};

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

export interface InvoiceDetailHeaderStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    title: TypographyProps;
    subTitle: TypographyProps;
    titleGridItem: GridItemProps;
    pdfButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    buttonWrapper?: InjectableCss;
    easyPayButton?: LinkPresentationProps;
    easyPayButtonWithinOverflowMenu?: LinkPresentationProps;
    iconStyle?: IconPresentationProps;
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    modalPDFWrapper?: InjectableCss;
}

interface InvoicesParam {
    invoicePdf: string;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    pdfButton: {
        css: css`
            ${iconsSharedStyles}
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
            align-items: center;
        `,
        width: [2, 2, 2, 6, 6],
    },
    titleGridItem: {
        width: [10, 10, 10, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    title: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 0;
        `,
    },
    subTitle: {
        variant: "h3",
        as: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    menuHiddenContainer: {
        above: "md",
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    easyPayButton: {
        css: css`
            ${iconsSharedStyles};
            > span {
                display: flex;
                align-items: center;
                color: ${getColor("text.main")} !important;
                &:hover {
                    color: ${getColor("primary.main")} !important;
                }
            }
        `,
    },
    easyPayButtonWithinOverflowMenu: {
        typographyProps: {
            css: css`
                padding: 0 !important;
                color: ${getColor("text.main")} !important;
            `,
        },
    },
    iconStyle: {
        size: 16,
        color: "primary.main",
        css: css`
            margin-right: 5px;
        `,
    },
    spinnerWrapper: {
        css: css`
            padding: 10px 15px;
        `,
    },
    spinner: {
        color: "primary.main",
        size: 25,
        css: css`
            margin: 0 auto;
        `,
    },
};

const styles = headerStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const InvoiceDetailHeader: FC<Props> = ({ fields, customSettings, getInvoicePdf }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const easyPayUrl = (customSettings as any).easyPayUrl;
    const toasterContext = React.useContext(ToasterContext);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [invoices, setInvoices] = React.useState([""]);

    if (!invoice) {
        return null;
    }

    const handlePDFClick = () => {
        setIsLoading(true);
        getInvoicePdf({
            invoiceNumber: invoice.invoiceNumber,
            onSuccess: (response: any) => {
                setInvoices(response.invoices);
                setIsLoading(false);

                if (response.invoices.length > 1) {
                    setIsModalOpen(true);
                } else {
                    handlePDFDownload(invoice.invoiceNumber, response?.invoices?.[0]);
                }
            },
            onError: (error: any) => {
                toasterContext.addToast({ body: translate("No Available PDF file"), messageType: "danger" });
                setIsLoading(false);
            },
        });
    };

    const handlePDFDownload = (invoiceNumber: string, invoice?: InvoicesParam) => {
        const pdfData = invoice?.invoicePdf;

        if (!pdfData) {
            return;
        }
        const fileName = `invoice ${invoiceNumber}.pdf`;
        const dataUri = `data:application/pdf;base64,${pdfData}`;
        const link = document.createElement("a");
        link.href = dataUri;
        link.target = "_blank"; 
        link.download = fileName;

        document.body.appendChild(link);

        const clickEvent = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: false,
        });
        link.dispatchEvent(clickEvent);

        document.body.removeChild(link);
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    const isCreditCard = invoice.properties.isCreditCard && invoice.properties.isCreditCard === "true";

    return (
        <>
            <GridContainer>
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.title}>
                        {`${isCreditCard ? translate("Receipt") : translate("Invoice #")} ${invoice.invoiceNumber}`}
                    </Typography>
                    {invoice.properties.einNumber && (
                        <Typography {...styles.subTitle}>
                            {translate("EIN #")} {invoice.properties.einNumber}
                        </Typography>
                    )}
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <Hidden {...styles.menuHiddenContainer}>
                        <OverflowMenu hasChildPortal position="end" {...actionOverflowMenu}>
                            {isLoading && (
                                <StyledWrapper {...styles.spinnerWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            )}
                            {!isLoading && (
                                <Clickable onClick={handlePDFClick} data-test-selector="invoiceDetails_PDF">
                                    {translate("PDF")}
                                </Clickable>
                            )}
                            <Link href="#" {...styles.easyPayButtonWithinOverflowMenu}>
                                {translate("Easy Pay")}
                            </Link>
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.buttonsHiddenContainer}>
                        <StyledWrapper {...styles.buttonWrapper}>
                            {isLoading && <LoadingSpinner {...styles.spinner} />}
                            {!isLoading && (
                                <Clickable
                                    {...styles.pdfButton}
                                    onClick={handlePDFClick}
                                    data-test-selector="invoiceDetails_PDF"
                                >
                                    <Icon src={Print} {...styles.iconStyle} />
                                    {translate("PDF")}
                                </Clickable>
                            )}
                        </StyledWrapper>
                        {easyPayUrl && (
                            <StyledWrapper {...styles.buttonWrapper}>
                                <Link href={easyPayUrl} {...styles.easyPayButton}>
                                    <Icon
                                        src={Invoices2}
                                        size={16}
                                        color="primary.main"
                                        css={css`
                                            margin-right: 3px;
                                        `}
                                    />
                                    {translate("Easy Pay")}
                                </Link>
                            </StyledWrapper>
                        )}
                    </Hidden>
                </GridItem>
            </GridContainer>
            <Modal
                headline={siteMessage("Custom_InvoicesPDF_Modal_Title", "Invoices PDF")}
                size={200}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                {invoices.map((invoiceFile: any, index) => (
                    <StyledWrapper key={index} {...styles.modalPDFWrapper}>
                        <Link
                            as="a"
                            href={`data:application/pdf;base64,${invoiceFile.invoicePdf}`}
                            download={`invoice ${invoice.invoiceNumber}_${index + 1}.pdf`}
                            onClick={modalCloseHandler}
                        >
                            {translate(`invoice ${invoice.invoiceNumber}_${index + 1}.pdf`)}
                        </Link>
                    </StyledWrapper>
                ))}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailHeader),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
